if (!customElements.get('product-form')) {
  const RP_CART_PERFORMANCE = window.CartPerformance || {
    createStartingMarker: () => undefined,
    measureFromMarker: () => undefined,
    measureFromEvent: () => undefined,
    measure: (_name, callback) => {
      if (typeof callback === 'function') callback();
    },
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (!this.form || !this.submitButton || !this.variantIdInput) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.mobileBuybar = this.querySelector('[data-rp-mobile-buybar]');
        this.mobileBuybarButton = this.mobileBuybar?.querySelector('.rp-mobile-buybar__button');
        this.variantIdInput.addEventListener('change', this.syncMobileBuybar.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.syncMobileBuybar();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;

        this.error = false;
        this.handleErrorMessage();
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.submitButton.dataset.rpWasDisabled = this.submitButton.disabled ?'true' : 'false';
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.loadingSpinner?.classList.remove('hidden');
        this.loadingSpinner?.removeAttribute('hidden');
        this.syncMobileBuybar(true, this.submitButtonText?.textContent);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        fetch(routes.cart_add_url, config)
          .then(async (response) => {
            const raw = await response.text();
            let payload = {};
            try {
              payload = raw ? JSON.parse(raw) : {};
            } catch {
              payload = { description: raw };
            }
            payload.__httpStatus = response.status;
            payload.__ok = response.ok;
            return payload;
          })
          .then((response) => {
            if (response.status || response.__ok === false) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              const responseErrors = typeof response.errors === 'string'
                ? response.errors
                : Object.values(response.errors || {}).flat().join(', ');
              const message = response.description || response.message || responseErrors || window.cartStrings?.error || 'Une erreur est survenue.';
              const classification = this.classifyAddError(response, message, Number(formData.get('quantity') || 1));
              this.handleErrorMessage(message);
              this.renderRetryAction(classification === 'transient');
              this.error = classification === 'stock';
              this.setVariantStockState(classification === 'stock', message);
              if (classification === 'quantity') this.applyQuantityLimit(response, message);
              if (classification === 'validation' && Number(response.__httpStatus) === 422) this.refreshVariantState();
              return;
            } else if (!this.cart || typeof this.cart.renderContents !== 'function') {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = RP_CART_PERFORMANCE.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              RP_CART_PERFORMANCE.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    RP_CART_PERFORMANCE.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              RP_CART_PERFORMANCE.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((error) => {
            console.error(error);
            this.error = false;
            this.handleErrorMessage(window.cartStrings?.error || 'Une erreur est survenue.');
            this.renderRetryAction(true);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-busy');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              if (this.submitButton.dataset.rpWasDisabled !== 'true') this.submitButton.disabled = false;
            }
            delete this.submitButton.dataset.rpWasDisabled;
            this.loadingSpinner?.classList.add('hidden');
            this.loadingSpinner?.setAttribute('hidden', '');
            this.syncMobileBuybar();

            RP_CART_PERFORMANCE.measureFromEvent('add:user-action', evt);
          });
      }

      classifyAddError(response, message, requestedQuantity) {
        const status = Number(response.__httpStatus || response.status || 0);
        const text = String(message || '').toLowerCase();
        if (status === 422) {
          const variantAvailable = response.available ?? response.variant?.available;
          const explicitStock = /sold out|out of stock|inventory unavailable|stock|rupture/.test(text) && !/quantity|maximum|max|only|seulement/.test(text);
          const quantityLimit = /maximum|max(?:imum)? quantity|quantity available|available quantity|quantit|inventaire insuffisant|only \d+|seulement \d+/.test(text);
          if (variantAvailable === false || explicitStock) return 'stock';
          if (quantityLimit) return 'quantity';
          const requiredField = /required|obligatoire|must be selected|choisir|selectionner/.test(text);
          const missingSellingPlan = /selling plan|plan de vente|subscription|abonnement/.test(text);
          if (missingSellingPlan) return 'selling-plan';
          if (requiredField) return 'required-field';
          return 'validation';
        }
        const soldOut = /sold out|out of stock|unavailable|épuis|rupture|indisponible|not available/.test(text);
        const quantityLimit = /quantity|quantité|maximum|max|only|seulement|stock|inventory/.test(text);
        if (soldOut) return 'stock';
        if (quantityLimit) return 'quantity';
        if (status === 409 || status === 429 || status >= 500 || status === 0) return 'transient';
        return 'error';
      }

      async refreshVariantState() {
        const variantId = this.variantIdInput?.value;
        const sectionId = this.dataset.sectionId;
        if (!variantId || !sectionId) return;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          url.searchParams.set('section_id', sectionId);
          const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
          if (!response.ok) return;
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const nextSubmit = documentFragment.querySelector('[data-rp-available]');
          if (!nextSubmit) return;
          const available = nextSubmit.dataset.rpAvailable === 'true';
          this.setVariantStockState(!available, available ? '' : 'Stock indisponible pour cette variante.');
        } catch (error) {
          // Keep the CTA usable when the validation response remains ambiguous.
        }
      }

      applyQuantityLimit(response, message) {
        const input = this.form?.querySelector('[name="quantity"]');
        const source = `${message || ''} ${JSON.stringify(response?.errors || '')}`;
        const match = source.match(/(?:only|seulement|maximum|max|stock)[^\d]{0,24}(\d+)/i);
        if (input && match) input.max = match[1];
        this.setVariantStockState(false, message);
      }

      setVariantStockState(soldOut, message = '') {
        const label = soldOut
          ? (window.variantStrings?.soldOut || 'Épuisé')
          : (this.submitButton.dataset.isPreorder === 'true'
            ? (window.variantStrings?.preorder || window.variantStrings?.addToCart)
            : window.variantStrings?.addToCart);
        this.submitButton.dataset.rpStockState = soldOut ? 'sold-out' : 'available';
        this.submitButton.classList.toggle('rp-stock-error', soldOut);
        this.submitButton.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
        this.submitButton.disabled = soldOut;
        if (this.submitButtonText && label) this.submitButtonText.textContent = label;
        this.syncMobileBuybar(soldOut, label);
        this.dispatchEvent(new CustomEvent('retroparty:variant-stock-state', {
          bubbles: true,
          detail: {
            variantId: this.variantIdInput?.value || '',
            available: !soldOut,
            message,
          },
        }));
      }

      renderRetryAction(show) {
        const wrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) return;
        wrapper.querySelector('[data-rp-product-form-retry]')?.remove();
        if (!show) return;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--tertiary product-form__retry';
        retry.dataset.rpProductFormRetry = 'true';
        retry.textContent = window.cartStrings?.retry || 'Réessayer';
        retry.addEventListener('click', () => this.form?.requestSubmit?.());
        wrapper.appendChild(retry);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        } else {
          this.renderRetryAction(false);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
           this.submitButton.setAttribute('disabled', 'disabled');
           this.submitButton.setAttribute('aria-disabled', 'true');
           if (text) this.submitButtonText.textContent = text;
        } else {
           this.submitButton.removeAttribute('disabled');
           this.submitButton.setAttribute('aria-disabled', 'false');
          this.submitButton.dataset.rpStockState = 'available';

          if (this.submitButton.dataset.isPreorder === 'true') {
            this.submitButtonText.textContent = window.variantStrings.preorder || window.variantStrings.addToCart;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }

        this.syncMobileBuybar(disable, this.submitButtonText.textContent);
      }

      syncMobileBuybar(
        disable = this.submitButton.hasAttribute('disabled') || this.submitButton.getAttribute('aria-disabled') === 'true',
        text = this.submitButtonText?.textContent
      ) {
        if (!this.mobileBuybarButton) return;

        const variantId = this.variantIdInput?.value || '';
        if (variantId) this.mobileBuybarButton.dataset.variantId = variantId;

        if (this.mobileBuybarButton.type !== 'submit') return;

        this.mobileBuybarButton.toggleAttribute('disabled', disable);
        this.mobileBuybarButton.setAttribute('aria-disabled', disable ?'true' : 'false');
        this.mobileBuybarButton.classList.toggle('loading', this.submitButton.classList.contains('loading'));

        if (this.mobileBuybarButton.type === 'submit' && text) {
          this.mobileBuybarButton.textContent = text;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
